import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, { useState, useEffect, useMemo } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import ReactPlayer from "react-player";
import Marquee from "react-fast-marquee";
import { useMediaQuery } from "react-responsive";

const TestimonialsVideoWritten = ({
	body,
	companyName,
	position,
	name,
	logo,
	video,
	testimonials,
	image,
	mediaType,
}) => {
	const logoImage = getImage(logo);

	// Client check to avoid SSR issues
	const [clientReady, setClientReady] = useState(false);
	useEffect(() => {
		setClientReady(true);
	}, []);

	// Media queries
	const isDesktop = useMediaQuery({ minWidth: 992 });
	const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
	const isMobile = useMediaQuery({ maxWidth: 767 });

	// Only show marquee if there are enough testimonials
	const showMarquee = useMemo(() => {
		if (!clientReady) return false;
		if (isDesktop) return testimonials.length > 3;
		if (isTablet) return testimonials.length > 2;
		if (isMobile) return testimonials.length > 1;
		return false;
	}, [clientReady, isDesktop, isTablet, isMobile, testimonials.length]);

	// Determine testimonial card width based on device
	const testimonialWidth = isDesktop ? "450px" : isTablet ? "50vw" : "95vw";

	return (
		<section style={testimonialSectionStyles}>
			<Container>
				<Row className="align-items-center g-3 g-lg-7">
					{/* --- Featured Testimonial --- */}
					<Col
						xs={12}
						lg={6}
						className="d-flex flex-column justify-content-center"
					>
						<p className="fs-5 text-primary outfit-bold text-center text-md-start pb-3">
							"{body}"
						</p>
						<div className="d-flex align-items-center">
							<div
								style={{ borderRight: "2px solid #00304B" }}
								className="d-block pe-3 py-2"
							>
								<p className="py-0 my-0 outfit-bold">{name}</p>
								<small
									style={{ color: "#00527B" }}
									className="text-primary calibri py-0 my-0"
								>
									{position}
									{position ? "," : ""} {companyName}
								</small>
							</div>
							<div className="ps-3">
								<img
									src={logo}
									alt={`${companyName} Logo`}
									style={logoStyles}
									className="mt-2"
								/>
							</div>
						</div>
					</Col>

					{/* --- Video or Image --- */}
					<Col
						xs={12}
						lg={{ span: 6, order: "first" }}
						className="text-center mb-4 mb-lg-0"
					>
						{mediaType === "Video" && (
							<div className="pb-0">
								<ReactPlayer
									className="video-border"
									url={video}
									controls
									width="100%"
									height="100%"
								/>
							</div>
						)}
						{mediaType === "Image" && (
							<div style={{ overflow: "hidden" }} className="video-border">
								<GatsbyImage
									image={image?.localFile?.childImageSharp?.gatsbyImageData}
									alt={image?.altText || "Image"}
									className="w-100"
								/>
							</div>
						)}
					</Col>
				</Row>

				{/* --- Additional Testimonials --- */}
			</Container>
			<Container fluid className="px-0 mx-0">
				<Row className="mt-5">
					<Col>
						<div className="pb-5" style={{ position: "relative" }}>
							{showMarquee ? (
								// --- Marquee view when there are enough testimonials ---
								<Marquee
									pauseOnHover
									gradient={false}
									innerContainerStyle={{
										display: "flex",
										alignItems: "center",
									}}
								>
									{testimonials.map((testimonial) => (
										<div
											className="pe-4"
											key={testimonial.id}
											style={{
												flex: "0 0 auto",
												width: testimonialWidth,
											}}
										>
											<Card
												style={testimonialCardStyles}
												className="text-start p-3"
											>
												<Card.Body>
													<blockquote>
														<p>"{testimonial.testimonialFields.review}"</p>
													</blockquote>
													<div className="d-flex align-items-center justify-content-start mt-3">
														<img
															className="me-3"
															src={
																testimonial.testimonialFields.image.sourceUrl
															}
															alt={testimonial.testimonialFields.image.altText}
															style={avatarStyles}
														/>
														<div className="ms-2">
															<p className="mb-0 outfit-bold">
																{testimonial.testimonialFields.name}
															</p>
															<small
																style={{ color: "#00527B" }}
																className="calibri"
															>
																{testimonial.testimonialFields.position},{" "}
																{testimonial.testimonialFields.companyName}
															</small>
														</div>
													</div>
												</Card.Body>
											</Card>
										</div>
									))}
								</Marquee>
							) : (
								// --- Static grid view if not enough testimonials for marquee ---
								<Row className="justify-content-center align-items-start">
									{testimonials.map((testimonial) => (
										<Col
											key={testimonial.id}
											xs={12}
											sm={12}
											md={6}
											lg={4}
											className="mb-4 d-flex justify-content-center"
										>
											<Card
												style={testimonialCardStyles}
												className="text-start p-3"
											>
												<Card.Body>
													<blockquote>
														<p>"{testimonial.testimonialFields.review}"</p>
													</blockquote>
													<div className="d-flex align-items-center justify-content-start mt-3">
														<img
															className="me-3"
															src={
																testimonial.testimonialFields.image.sourceUrl
															}
															alt={testimonial.testimonialFields.image.altText}
															style={avatarStyles}
														/>
														<div className="ms-2">
															<p className="mb-0 outfit-bold">
																{testimonial.testimonialFields.name}
															</p>
															<small
																style={{ color: "#00527B" }}
																className="calibri"
															>
																{testimonial.testimonialFields.position},{" "}
																{testimonial.testimonialFields.companyName}
															</small>
														</div>
													</div>
												</Card.Body>
											</Card>
										</Col>
									))}
								</Row>
							)}
						</div>
					</Col>
				</Row>
			</Container>
		</section>
	);
};

// ---------------------------
// Inline Styles
// ---------------------------
const testimonialSectionStyles = {
	padding: "50px 0",
};

const testimonialCardStyles = {
	border: "1px solid #CEE4FF",
	borderRadius: "12px",
};

const avatarStyles = {
	width: "50px",
};

const logoStyles = {
	width: "80px",
	height: "auto",
	marginTop: "1rem",
};

export default TestimonialsVideoWritten;
